/**
 * Working Billing Claim — session write tracker (GDN-2614, audit option U3).
 *
 * Records which encounters had their working claim written this browser session,
 * and *why*. Two consumers ask this module two different questions, and they
 * need different answers:
 *
 *   `useStreamedBillingClaim` asks "may I overwrite the claim in the cache?"
 *      → `hasWorkingClaimWrite`. ANY local write counts. A claim pushed over the
 *        note-generation stream must never clobber codes the provider has
 *        already put on screen, however they got there.
 *
 *   `useSuggestionExpectation` asks "should the suggestion UI stand down?"
 *      → `hasWorkingClaimEdit`. Only a provider-originated edit counts.
 *        Accepting the model's own suggestion is agreement, not an independent
 *        edit — it must not tear down the pending/arrival/timeout UI for the
 *        *other* code type, whose suggestion may still be in flight. CPT
 *        recommendations come from the audit pipeline and ICD-10 from the
 *        suggestion pipeline, on independent timing; before the split, accepting
 *        either one silently killed the other's shimmer, stopped the poll, and
 *        left the provider to type the codes by hand (GDN-2618).
 *
 * Once an encounter is marked, its origin can only be upgraded — a genuine
 * provider edit after an accepted suggestion promotes the record to
 * `provider_edit` and never falls back. So the weaker state can't get stuck: the
 * moment the provider actually types, full U3 suppression applies.
 *
 * RESIDUAL, DELIBERATELY NOT FIXED HERE: this tracker is keyed per *encounter*,
 * not per code type. A genuine manual edit to either CPT or ICD-10 therefore
 * still suppresses the suggestion UI for both. That is the conservative U3
 * reading — once the provider is editing billing codes at all, nothing may move
 * under their cursor — and narrowing it would mean threading a code type through
 * this tracker, `useSuggestionExpectation`, `NoteChatView`, and the `suggestion`
 * prop on `NoteMetadata`, end to end. Tracked separately if we want it.
 *
 * Deliberately session-scoped (module lifetime = page lifetime). A reload
 * legitimately re-opens the question of whether a suggestion should surface.
 */

/**
 * Why the working claim was written.
 *
 * - `provider_edit`: the provider changed the codes themselves. Their edit owns
 *   the screen; all suggestion automation stands down.
 * - `accepted_suggestion`: the provider accepted codes the model proposed. It is
 *   still a write (nothing may overwrite it), but it is not a signal that they
 *   want the remaining suggestions to stop.
 */
export type WorkingClaimWriteOrigin = 'provider_edit' | 'accepted_suggestion';

const writtenEncounters = new Map<string, WorkingClaimWriteOrigin>();
const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach(listener => listener());
}

/**
 * Record a local write to this encounter's working billing claim.
 *
 * Defaults to `provider_edit`, so every existing call site keeps today's
 * meaning; only the two suggestion-accept handlers opt into the weaker origin.
 */
export function markWorkingClaimEdited(
  encounterId: string | null | undefined,
  origin: WorkingClaimWriteOrigin = 'provider_edit',
): void {
  if (!encounterId) return;

  const current = writtenEncounters.get(encounterId);
  if (current === origin) return;
  // Upgrade only. An accepted suggestion never demotes a real provider edit.
  if (current === 'provider_edit') return;

  writtenEncounters.set(encounterId, origin);
  emit();
}

/**
 * Whether this encounter's working claim was written at all this session —
 * by a provider edit or by accepting a suggestion. Use this to decide whether
 * something may overwrite what is on screen.
 */
export function hasWorkingClaimWrite(encounterId: string | null | undefined): boolean {
  return Boolean(encounterId) && writtenEncounters.has(encounterId as string);
}

/**
 * Whether the provider *edited* this encounter's working claim this session.
 * Accepting the model's suggestion does not count. Use this to decide whether
 * suggestion UI should stand down.
 */
export function hasWorkingClaimEdit(encounterId: string | null | undefined): boolean {
  return (
    Boolean(encounterId) && writtenEncounters.get(encounterId as string) === 'provider_edit'
  );
}

/** Subscribe to write-tracker changes. Returns an unsubscribe function. */
export function subscribeToWorkingClaimEdits(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: clear session state between cases. */
export function resetWorkingClaimEdits(): void {
  writtenEncounters.clear();
  emit();
}
